import {
	alpha,
	Box,
	Button,
	IconButton,
	Skeleton,
	Stack,
	SvgIcon,
	type SxProps,
	Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../../assets/styles/themes/tokens';
import { colors } from '../../../assets/styles/themes/variables';
import getMuiIcon, { type IconName } from '../../../utils/getMuiIcon';
import useScreenSize from '../../../utils/useScreenSize';

interface ArrowNavigationButtonProps {
	dataTestId: string;
	iconName: IconName;
	onClick: () => void;
	sxProps?: SxProps;
	ariaLabel: string;
	disabled?: boolean;
}

interface CarouselHeaderProps {
	title: string;
	onSlideNext: () => void;
	onSlidePrev: () => void;
	onShowAll?: () => void;
	loading?: boolean;
}

const ArrowNavigationButton: React.FC<ArrowNavigationButtonProps> = (props) => {
	const { iconName, sxProps, onClick, dataTestId, ariaLabel, disabled } = props;
	return (
		<IconButton
			data-testid={dataTestId}
			color="secondary"
			size="small"
			onClick={onClick}
			aria-label={ariaLabel}
			disabled={disabled}
		>
			<SvgIcon
				sx={{
					width: 20,
					height: 20,
					color: palette.action.active,
					...sxProps,
				}}
				component={getMuiIcon(iconName)}
			/>
		</IconButton>
	);
};

const CarouselHeader: React.FC<CarouselHeaderProps> = (props) => {
	const { t } = useTranslation();
	const { isMdScreen, isLgScreen } = useScreenSize();
	const isDesktopScreen = isMdScreen || isLgScreen;
	const { title, onSlideNext, onSlidePrev, onShowAll, loading } = props;

	return (
		<Box
			data-testid="carousel-header"
			sx={{
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'space-between',
				// Top-align so the actions stay at the first title line even when the
				// title wraps to two lines (e.g. the Studyflix row) instead of drifting
				// down with a vertically-centered taller header.
				alignItems: 'flex-start',
				paddingY: spaces.sm,
				gap: spaces.xs,
			}}
		>
			{loading ? (
				<Typography variant="h6" sx={{ width: '100%', maxWidth: 280 }}>
					<Skeleton variant="text" data-testid="carousel-title-skeleton" />
				</Typography>
			) : (
				<Typography variant="h6" color={palette.text.primary}>
					{title}
				</Typography>
			)}
			<Box sx={{ display: 'flex', flexDirection: 'row', gap: spaces.xs }}>
				{onShowAll && !loading && (
					<Button
						variant="outlined"
						color="primary"
						size="small"
						onClick={onShowAll}
						sx={{
							borderColor: alpha(colors.blue700, 0.48),
							width: 'max-content',
						}}
					>
						{t('Show all')}
					</Button>
				)}
				{isDesktopScreen && (
					<Stack direction="row" alignItems="center" justifyContent="space-between" gap={spaces.xs}>
						<ArrowNavigationButton
							dataTestId="arrow-navigation-button-left"
							iconName="arrowLeft"
							onClick={onSlidePrev}
							ariaLabel={t('Previous slide')}
							disabled={loading}
						/>
						<ArrowNavigationButton
							dataTestId="arrow-navigation-button-right"
							iconName="arrowLeft"
							sxProps={{ transform: 'rotate(180deg)' }}
							onClick={onSlideNext}
							ariaLabel={t('Next slide')}
							disabled={loading}
						/>
					</Stack>
				)}
			</Box>
		</Box>
	);
};

export default CarouselHeader;
